import { useEffect, useMemo, useRef, useState } from "preact/hooks"

import type {
  EmbedKind,
  RulePayload,
  WidgetPayload,
} from "@workspace/contracts/embed"

import type { Api } from "./api"
import { lookOf, type Look } from "./appearance"
import type { Reporter } from "./events"
import type { PageContext } from "./host"
import { runRules } from "./rules"
import { BannerSurface } from "./surfaces/banner"
import { ChatSurface } from "./surfaces/chat"
import { ExitPopupSurface } from "./surfaces/exit-popup"
import { TestimonialSurface } from "./surfaces/testimonial"

/**
 * One surface on the page: its look, its rules, its open state. The rules
 * say WHEN it appears (`rules.ts`): a surface with rules is not on the
 * page at all until the first match fires, and then it SHOWS - that is
 * what the dashboard's rule sentence promises ("When …, show <surface>"),
 * so a firing is the show, and `actionConfig.open` (frozen `false` by the
 * composer) adds nothing to it. What "shows" means is the surface's: a
 * chat bubble's panel unfolds, a popup comes up, a banner or a testimonial
 * appears. `autoStart` puts the visitor in the composer, `pinned` takes the
 * close away. With no rules a surface rests - the launcher alone, the
 * strip and the card straight away, the popup only when the visitor moves
 * to leave (its namesake, the default when nothing else is said).
 */

export interface SurfaceProps {
  payload: WidgetPayload
  look: Look
  api: Api
  /** The tag this surface arrived under - the widget itself, or its group. */
  embed: { kind: EmbedKind; id: string }
  page: PageContext
  reporter: Reporter
  /** The surface is showing (the panel, the popup, the strip, the card). */
  open: boolean
  setOpen: (open: boolean) => void
  /** A rule pinned it: no close, the launcher does not fold it away. */
  pinned: boolean
  /** Bumped when a rule asks to `autoStart` - the composer takes focus. */
  focus: number
}

export interface Kick {
  action: RulePayload["actionConfig"]
  n: number
}

/** Where a surface with no rules starts: the launcher folded, the rest out. */
function restingOpen(payload: WidgetPayload): boolean {
  switch (payload.config.type) {
    case "chat":
    case "exit-popup":
      return false
    case "banner":
    case "testimonial":
      return true
  }
}

/** A popup with nothing said fires on exit intent, once a visit. */
function rulesOf(payload: WidgetPayload): RulePayload[] {
  if (payload.rules.length > 0 || payload.config.type !== "exit-popup") {
    return payload.rules
  }
  return [
    {
      id: `default:${payload.id}`,
      trigger: "exit_intent",
      triggerParams: {},
      conditions: [],
      once: true,
      actionConfig: { open: false, autoStart: false, pinned: false },
    },
  ]
}

export function Surface({
  payload,
  api,
  embed,
  page,
  reporter,
  hostDark,
  kick = null,
}: {
  payload: WidgetPayload
  api: Api
  embed: { kind: EmbedKind; id: string }
  page: PageContext
  reporter: Reporter
  hostDark: boolean
  /** A group rule's action, handed down when it fires (`n` makes each firing new). */
  kick?: Kick | null
}) {
  const look = useMemo(
    () => lookOf(payload.config, hostDark),
    [payload, hostDark]
  )
  const rules = useMemo(() => rulesOf(payload), [payload])
  // No rules: on the page from the start. Rules: not until one fires.
  const [shown, setShown] = useState(rules.length === 0)
  const [open, setOpenState] = useState(
    () => rules.length === 0 && restingOpen(payload)
  )
  const [pinned, setPinned] = useState(false)
  const [focus, setFocus] = useState(0)
  const wasOpen = useRef(open)

  const setOpen = (next: boolean) => {
    setOpenState(next)
    if (next === wasOpen.current) return
    wasOpen.current = next
    reporter.report(next ? "surface.opened" : "surface.closed", {
      widgetId: payload.id,
    })
  }

  /** A rule fired - its own, or its group's: show, with the flags it carries. */
  const show = ({ autoStart, pinned: pins }: RulePayload["actionConfig"]) => {
    setShown(true)
    setPinned(pins)
    setOpen(true)
    if (autoStart) setFocus((n) => n + 1)
  }

  useEffect(
    () =>
      runRules(rules, page, (rule) => {
        reporter.report("rule.fired", {
          widgetId: payload.id,
          payload: { ruleId: rule.id, trigger: rule.trigger },
        })
        show(rule.actionConfig)
      }),
    // Armed once per payload: the rules and the page do not change under a mount.
    [payload.id]
  )

  useEffect(() => {
    if (kick) show(kick.action)
  }, [kick])

  if (!shown) return null

  const props: SurfaceProps = {
    payload,
    look,
    api,
    embed,
    page,
    reporter,
    open,
    setOpen,
    pinned,
    focus,
  }
  switch (payload.config.type) {
    case "chat":
      return <ChatSurface {...props} />
    case "exit-popup":
      return <ExitPopupSurface {...props} />
    case "banner":
      return <BannerSurface {...props} />
    case "testimonial":
      return <TestimonialSurface {...props} />
  }
}
